import { defineStore } from 'pinia'
import { useAuthStore } from '@/stores/auth'
import { useSettingsStore } from '@/stores/settings'

export const CURRENT_TUTORIAL_VERSION = 8

export function pageTutorialSteps(path = '') {
  const normalizedPath = String(path || '').replace(/\/$/, '') || '/'
  let guide

  if (normalizedPath === '/home') {
    guide = {
      id: 'page-home',
      title: 'The family home page',
      body: 'This dashboard combines the family world map, relationship network, people search, notable figures, historical properties, and heirlooms. Use the filters and interactive graphics to explore the archive.'
    }
  } else if (/^\/family_members(?:\/[^/]+)?\/settings$/.test(normalizedPath)) {
    guide = {
      id: 'page-settings',
      title: 'Your settings',
      body: 'This page controls accessibility, language and currency, notification channels, text-to-speech, privacy consent, password, two-factor authentication, and passkeys.'
    }
  } else if (/^\/family_members\/[^/]+\/edit$/.test(normalizedPath)) {
    guide = {
      id: 'page-profile-edit',
      title: 'Edit a family profile',
      body: 'Use this page to propose permitted changes to personal details, biography, relationships, locations, and media. Changes that require review are sent through the approval process.'
    }
  } else if (normalizedPath === '/family_members') {
    guide = {
      id: 'page-directory',
      title: 'The family directory',
      body: 'The Directory includes family members who have approved GDPR consent. Search by name, country, email address, or phone number—for example, to find relatives near a place you plan to visit.'
    }
  } else if (/^\/family_members\/[^/]+$/.test(normalizedPath)) {
    guide = {
      id: 'page-profile',
      title: 'A family profile',
      body: 'Profile tabs organize biography, parents, spouses, siblings and children, the family tree, world locations and migration routes, and profile or family media.'
    }
  } else if (/^\/branches\/[^/]+$/.test(normalizedPath)) {
    guide = {
      id: 'page-branch',
      title: 'A family branch',
      body: 'This branch brings together its originators, members, family tree, world map, history, migration routes, notable people, properties, heirlooms, and role-dependent administration.'
    }
  } else if (normalizedPath === '/branches') {
    guide = {
      id: 'page-branches',
      title: 'Family branches',
      body: 'The Branches page shows every family branch. Select a branch to open its family tree, map, history, migration path, notable figures, historical properties, heirlooms, and administration tools.'
    }
  } else if (/^\/reunions\/[^/]+\/rsvp\/[^/]+$/.test(normalizedPath)) {
    guide = {
      id: 'page-reunion-rsvp',
      title: 'Reunion RSVP',
      body: 'Confirm attendance and the requested reunion details here. Your response is associated with the invited person shown on this page.'
    }
  } else if (/^\/reunions\/[^/]+$/.test(normalizedPath)) {
    guide = {
      id: 'page-reunion',
      title: 'A family reunion',
      body: 'This reunion page contains its schedule and details, locations and routes, committee, invitations and RSVPs, media and tagging tools, and reunion-book publishing when available.'
    }
  } else if (normalizedPath === '/reunions') {
    guide = {
      id: 'page-reunions',
      title: 'Family reunions',
      body: 'Reunions are organized by year. For safety, a reunion is not visible to everyone until after the event. Any family member can create a reunion.'
    }
  } else if (/^\/heirlooms\/[^/]+$/.test(normalizedPath)) {
    guide = {
      id: 'page-heirloom',
      title: 'An heirloom record',
      body: 'Review the heirloom story, media, provenance and ownership route, related people, marketplace listing, and requests from this page.'
    }
  } else if (normalizedPath === '/heirlooms') {
    guide = {
      id: 'page-heirlooms',
      title: 'Family heirlooms',
      body: 'Browse the family heirloom archive here. Anyone can create an heirloom; doing so also requests the Archivist role so the record can be administered.'
    }
  } else if (/^\/properties\/[^/]+\/requests\/[^/]+\/cancel$/.test(normalizedPath)) {
    guide = {
      id: 'page-property-request',
      title: 'Property request',
      body: 'Review the property request shown here before confirming its cancellation.'
    }
  } else if (/^\/properties\/[^/]+$/.test(normalizedPath)) {
    guide = {
      id: 'page-property',
      title: 'A family property',
      body: 'This record includes property history, media, provenance, availability, contact details, associated people, and nearby points of interest.'
    }
  } else if (normalizedPath === '/properties') {
    guide = {
      id: 'page-properties',
      title: 'Family and historical properties',
      body: 'This page lists historical and currently family-owned properties, including the location, property name, and first paragraph of the description. Historical properties are marked on their cards.'
    }
  } else if (/^\/recipes\/[^/]+$/.test(normalizedPath)) {
    guide = {
      id: 'page-recipe',
      title: 'A family recipe',
      body: 'Follow the ingredients, equipment, steps, notes and variations here. Use the cooking view when you want a focused step-by-step presentation.'
    }
  } else if (normalizedPath === '/recipes') {
    guide = {
      id: 'page-recipes',
      title: 'Family recipes',
      body: 'Recipe cards show country of origin, preparation and cooking times, servings, allergens, and counts for ingredients, equipment, steps, and variations. Anyone can add a recipe; doing so requests the Chef role.'
    }
  } else if (normalizedPath === '/store/checkout/success') {
    guide = {
      id: 'page-checkout-success',
      title: 'Order confirmation',
      body: 'This page confirms the completed checkout and provides the available order information and next steps.'
    }
  } else if (normalizedPath === '/store/checkout') {
    guide = {
      id: 'page-checkout',
      title: 'Store checkout',
      body: 'Review the cart, delivery address, available product or book options, totals, and payment details before placing the order.',
      steps: [
        {
          title: 'Shipping address',
          body: 'Complete every highlighted delivery field. Shipping and tax estimates calculate after the required address information is present.',
          target: '[data-tutorial="checkout-shipping"]',
          requireTarget: '[data-tutorial="checkout-shipping"]'
        },
        {
          title: 'Order summary',
          body: 'Review the highlighted cart summary, quantities, shipping estimate, and total before payment.',
          target: '[data-tutorial="checkout-summary"]',
          requireTarget: '[data-tutorial="checkout-summary"]'
        },
        {
          title: 'Pay now',
          body: 'After the delivery details and payment method are complete, use the highlighted button to place the order.',
          target: '[data-tutorial="checkout-pay"]',
          requireTarget: '[data-tutorial="checkout-pay"]'
        }
      ]
    }
  } else if (/^\/store\/books\/[^/]+$/.test(normalizedPath)) {
    guide = {
      id: 'page-book',
      title: 'A reunion book',
      body: 'Review the completed reunion book and choose the available print size, interior, paper, binding, and finish options before ordering.'
    }
  } else if (/^\/store\/products\/[^/]+$/.test(normalizedPath)) {
    guide = {
      id: 'page-product',
      title: 'A store product',
      body: 'Choose the available product options and quantity here, then add the configured item to the cart.',
      steps: [
        {
          title: 'Product options',
          body: 'Choose each available option, such as color or size, in the highlighted area.',
          target: '[data-tutorial="product-options"]',
          requireTarget: '[data-tutorial="product-options"]'
        },
        {
          title: 'Quantity',
          body: 'Set the number of configured items you want to order.',
          target: '[data-tutorial="product-quantity"]',
          requireTarget: '[data-tutorial="product-quantity"]'
        },
        {
          title: 'Add to cart',
          body: 'Use the highlighted action to add the configured product to your cart.',
          target: '[data-tutorial="product-add-cart"]',
          requireTarget: '[data-tutorial="product-add-cart"]'
        },
        {
          title: 'Open the cart',
          body: 'Use the cart button to review selected items and continue to checkout.',
          target: '[data-tutorial="store-cart"]',
          requireTarget: '[data-tutorial="store-cart"]'
        }
      ]
    }
  } else if (normalizedPath === '/store') {
    guide = {
      id: 'page-store',
      title: 'The family store',
      body: 'The store is currently a demo until the family has a business entity for tax purposes. It contains family products, heirloom marketplace listings, and family books.'
    }
  } else if (normalizedPath === '/site_admin') {
    guide = {
      id: 'page-admin',
      title: 'Administration',
      body: 'Administration is visible only to authorized Site Administrators, Branch Administrators, Archivists, Executors, Genealogists, and Chefs. The available tabs change with your assigned roles.'
    }
  } else if (/^\/gdpr\/(approve|reject)$/.test(normalizedPath)) {
    guide = {
      id: 'page-privacy-response',
      title: 'Privacy consent',
      body: 'This page records the privacy-consent decision for the signed-in person and explains the result.'
    }
  } else {
    guide = {
      id: 'page-current',
      title: 'About this page',
      body: 'The tutorial remains available throughout the signed-in website. Use the highlighted header controls to move between sections or restart this guide whenever you need it.'
    }
  }

  const overview = {
    ...guide,
    steps: undefined,
    path: normalizedPath,
    target: '[data-tutorial="page-content"]'
  }

  return [
    overview,
    ...(guide.steps || []).map((step, index) => ({
      ...step,
      id: `${guide.id}-step-${index + 1}`,
      path: normalizedPath
    }))
  ]
}

function mediaUploadTutorialSteps(title, body) {
  const imageTabTarget =
    '[data-tutorial-tab-group="media-types"] [data-tutorial-tab="image"]'

  return [
    {
      title,
      body,
      target: '[data-tutorial="media-section"]'
    },
    {
      title: 'Choose an image',
      body: 'The preview-only family image demonstrates the completed upload form without uploading or saving anything. Images can be published in family books; videos, documents, and audio can also be stored.',
      target: '[data-tutorial="media-example"]',
      requireTarget: '[data-tutorial="media-add"], [data-tutorial="media-form"]',
      activationTarget: imageTabTarget,
      activateOnCurrentPath: true,
      scrollTargetIntoView: true,
      optionalActivationTarget: '[data-tutorial="media-add"]',
      dispatchTarget: '[data-tutorial="media-form"]',
      dispatchEvent: 'tutorial:show-media-example'
    },
    {
      title: 'Title the image',
      body: 'Enter a short, recognizable title for the image. The tutorial has filled this field with an example.',
      target: '[data-tutorial="media-title"]',
      requireTarget: '[data-tutorial="media-add"], [data-tutorial="media-form"]',
      activationTarget: imageTabTarget,
      activateOnCurrentPath: true,
      scrollTargetIntoView: true
    },
    {
      title: 'Provide alternative text',
      body: 'Describe what is visible so people can understand the image when it cannot be displayed or media is disabled.',
      target: '[data-tutorial="media-alt-text"]',
      requireTarget: '[data-tutorial="media-add"], [data-tutorial="media-form"]',
      activationTarget: imageTabTarget,
      activateOnCurrentPath: true,
      scrollTargetIntoView: true
    },
    {
      title: 'Choose the image type',
      body: 'Select Profile, Family, Group, Crest, Property, or World View to identify how the image will be used.',
      target: '[data-tutorial="media-image-type"]',
      requireTarget: '[data-tutorial="media-add"], [data-tutorial="media-form"]',
      activationTarget: imageTabTarget,
      activateOnCurrentPath: true,
      scrollTargetIntoView: true
    },
    {
      title: 'Place numbered tags',
      body: 'Select each person in the image from left to right. Every marker receives a number; selecting a marker again removes it.',
      target: '[data-tutorial="media-example"]',
      requireTarget: '[data-tutorial="media-add"], [data-tutorial="media-form"]',
      activationTarget: imageTabTarget,
      activateOnCurrentPath: true,
      scrollTargetIntoView: true
    },
    {
      title: 'Match each tag to a person',
      body: 'Search for people in the same order as the image markers. Each selected-person card displays the same number as its marker, making every connection explicit.',
      target: '[data-tutorial="media-people"]',
      requireTarget: '[data-tutorial="media-add"], [data-tutorial="media-form"]',
      activationTarget: imageTabTarget,
      activateOnCurrentPath: true,
      scrollTargetIntoView: true
    }
  ]
}

const pageTabGuides = {
  'page-home': {
    world_map: [
      {
        title: 'Family World Map',
        body: 'Countries are colored by family branch. When multiple branches have family information in one country, the country uses a multi-color pattern.',
        target: '[data-tutorial-panel="world_map"] [data-tutorial="world-map-canvas"]'
      },
      {
        title: 'Birth Country',
        body: 'Birth Country colors countries using the branches of family members who were born there.',
        target: '[data-tutorial-panel="world_map"] [data-tutorial="world-map-birth_country"]',
        activationTarget: '[data-tutorial-panel="world_map"] [data-tutorial="world-map-birth_country"]'
      },
      {
        title: 'Current Country',
        body: 'Current Country shows where living family members currently reside.',
        target: '[data-tutorial-panel="world_map"] [data-tutorial="world-map-current_country"]',
        activationTarget: '[data-tutorial-panel="world_map"] [data-tutorial="world-map-current_country"]'
      },
      {
        title: 'Death Country',
        body: 'Death Country shows the countries where deceased family members died.',
        target: '[data-tutorial-panel="world_map"] [data-tutorial="world-map-death_country"]',
        activationTarget: '[data-tutorial-panel="world_map"] [data-tutorial="world-map-death_country"]'
      },
      {
        title: 'Grave Country',
        body: 'Grave Country shows the countries containing recorded family graves.',
        target: '[data-tutorial-panel="world_map"] [data-tutorial="world-map-grave_country"]',
        activationTarget: '[data-tutorial-panel="world_map"] [data-tutorial="world-map-grave_country"]'
      },
      {
        title: 'Marriage Country',
        body: 'Marriage Country shows the countries where recorded family marriages took place.',
        target: '[data-tutorial-panel="world_map"] [data-tutorial="world-map-married_country"]',
        activationTarget: '[data-tutorial-panel="world_map"] [data-tutorial="world-map-married_country"]'
      },
      {
        title: 'Divorce Country',
        body: 'Divorce Country shows the countries where recorded family divorces took place.',
        target: '[data-tutorial-panel="world_map"] [data-tutorial="world-map-divorce_country"]',
        activationTarget: '[data-tutorial-panel="world_map"] [data-tutorial="world-map-divorce_country"]'
      },
      {
        title: 'Change the map era',
        body: 'Use this dropdown to change from the Modern Map to an available historical era.',
        target: '[data-tutorial-panel="world_map"] [data-tutorial="world-map-era"]'
      }
    ],
    family_web: [
      {
        title: 'Family Web',
        body: 'The Family Web displays the family tree as a node network. Branch colors identify people and relationship lines throughout the network.',
        target: '[data-tutorial-panel="family_web"] [data-tutorial="family-web-canvas"]'
      },
      {
        title: 'Search for Ian Stavros',
        body: 'Enter “Ian Stavros” in the Family Web search and select Search to locate him in the network.',
        target: '[data-tutorial-panel="family_web"] [data-tutorial="family-web-search"]',
        inputTarget: '[data-tutorial-panel="family_web"] [data-tutorial="family-web-search"]',
        inputValue: 'Ian Stavros',
        submitTarget: '[data-tutorial-panel="family_web"] [data-tutorial="family-web-search-submit"]'
      },
      {
        title: 'The indicating echo',
        body: 'The selected person is centered and marked by a colored, repeating echo ring. The enlarged glowing node and its information card confirm exactly who was found.',
        target: '[data-tutorial-panel="family_web"] [data-tutorial="family-web-canvas"]'
      }
    ],
    notable_figures: [
      ['Notable family members', 'This tab shows notable family members from every branch. Only Branch Administrators can designate notable members.']
    ],
    historical_properties: [
      ['Historical properties', 'This tab brings together historical family properties from every branch.']
    ],
    heirlooms: [
      ['Family heirlooms', 'This tab brings together family heirlooms from every branch.']
    ]
  },
  'page-profile': {
    biography: [
      ['Profile locations', 'The profile map can show birth, current, marriage, and divorce locations. For deceased family members, it can also show death and grave locations.'],
      {
        title: 'Edit the biography',
        body: 'Select the highlighted pencil icon to edit the biography. The first paragraph is used in the published family book. Use Save when the biography is complete, or Cancel to leave without saving.',
        target: '[data-tutorial="profile-biography-edit"]',
        requireTarget: '[data-tutorial="profile-biography-edit"]'
      }
    ],
    tree: [
      ['Your family tree', 'The family tree shows where this person fits in the family and how they connect to their ancestors. Their card is outlined to make it easy to find.'],
      ['Explore the tree', 'Select any person card to open that family member’s profile. Use a mouse or touchpad to zoom the tree up to 100%.']
    ],
    migration_path: [
      ['Profile locations', 'Add locations to build the person’s migration history.'],
      ['Migration routes', 'Add routes between two saved locations. A location must be added before it can be used as a route endpoint.']
    ],
    media: mediaUploadTutorialSteps(
      'Profile media',
      'Media can be uploaded by the profile owner, a Branch Administrator, or a parent when the person is under 18.'
    )
  },
  'page-profile-edit': {
    details: [
      ['Personal details', 'Update the profile image, name, sex, birth information, living status, and current location here. Deceased profiles can also include death, cremation, grave, and cemetery information.'],
      ['Titles and contact information', 'This section also contains noble titles, other titles, email, and phone information when those fields are available to the editor.']
    ],
    parents: [
      ['Parents', 'Review existing parents or add a parent by selecting the family member, parent role, and relationship type.']
    ],
    siblings: [
      ['Siblings', 'Review existing siblings or add a sibling and identify the sibling relationship type.']
    ],
    spouses: [
      ['Spouses', 'Review or add spouses and record marriage information. When applicable, divorce location and date can also be recorded.']
    ],
    children: [
      ['Children', 'Review existing children or add a child and identify the parental relationship.']
    ]
  },
  'page-settings': {
    personal: [
      ['Personal and accessibility settings', 'Most display and accessibility preferences can also be changed from the Accessibility icon in the header.'],
      {
        title: 'Preferred currency',
        body: 'Choose a preferred currency to see an approximate conversion when a price is listed in another currency.',
        target: '[data-tutorial="settings-currency"]',
        requireTarget: '[data-tutorial="settings-currency"]'
      },
      {
        title: 'Website language',
        body: 'Choose a preferred website language. Translation support is not fully connected yet.',
        target: '[data-tutorial="settings-language"]',
        requireTarget: '[data-tutorial="settings-language"]'
      }
    ],
    notifications: [
      {
        title: 'Notification preferences',
        body: 'Choose which notifications to receive and how to receive them. Text messaging exists in the system but is not currently enabled because each message has a cost.',
        target: '[data-tutorial="settings-notifications"]'
      }
    ],
    reader: [
      {
        title: 'Website Reader',
        body: 'Enable the Website Reader here or from the Accessibility control. Available voices are provided by the browser.',
        target: '[data-tutorial="settings-reader"]'
      }
    ],
    gdpr: [
      {
        title: 'Privacy and GDPR',
        body: 'Use the consent actions shown here to approve or reject GDPR consent at any time. Rejecting hides your profile from other users and the Directory, but you can still appear in family trees and reunion invitation lists.',
        target: '[data-tutorial="settings-gdpr"]'
      }
    ],
    security: [
      {
        title: 'Two-factor authentication',
        body: 'Use this control to add login security with an authenticator app, email, or text message. Text-message authentication is not currently enabled.',
        target: '[data-tutorial="settings-two-factor"]'
      },
      {
        title: 'Passkeys',
        body: 'Use Add Passkey to add a fingerprint or another device passkey as an additional login method.',
        target: '[data-tutorial="settings-passkeys"]'
      },
      {
        title: 'Password',
        body: 'Select the highlighted button to reset your password.',
        target: '[data-tutorial="settings-password"]'
      }
    ]
  },
  'page-branch': {
    map: [
      ['Branch world map', 'This map has the same controls as the Home map, but shows branch-specific locations and coloring.']
    ],
    tree: [
      ['Branch family tree', 'The branch family tree is zoomable. Select a person’s card to open their profile.']
    ],
    history: [
      ['Branch history', 'Branch history can be edited by the current Branch Head, Branch Administrators, a living Branch Originator, and Genealogists. Edits require Branch Administrator approval, and administrators cannot approve their own edits.']
    ],
    migration_path: [
      ['Branch migration path', 'This tab combines the migration paths submitted by members of the branch.']
    ],
    notable_figures: [
      ['Branch notable figures', 'This tab shows notable historical figures associated with this branch.']
    ],
    historical_properties: [
      ['Branch historical properties', 'This tab shows historical properties associated with this branch.']
    ],
    heirlooms: [
      ['Branch heirlooms', 'This tab shows heirlooms associated with this branch.']
    ],
    new_member_applications: [
      ['Branch administration', 'This tab is available only to administrators of the branch. A new member must have an email address before approval.'],
      ['Notable figures', 'Branch Administrators use this area to designate notable figures for their branch.'],
      ['Change approvals', 'Branch Administrators approve or reject proposed changes here. Open the difference view to review exactly what changed.']
    ]
  },
  'page-reunion': {
    planning_committee: [
      ['Planning Committee', 'Search for a family member and assign their committee role. The committee list shows who participated in reunion planning and in what capacity.'],
      {
        title: 'Publish the reunion book',
        body: 'Planning Committee members can publish the reunion book. Published books appear in the Books tab of the family store.',
        target: '[data-tutorial="reunion-publish"]',
        requireTarget: '[data-tutorial="reunion-publish"]',
        requireTargetImmediately: true
      }
    ],
    locations: [
      ['Reunion locations and routes', 'This tab shows reunion locations and how the family traveled between them by train, automobile, boat, or plane.']
    ],
    guests: [
      ['Invitations and RSVPs', 'Invitations are sent to all living family members regardless of GDPR status. The guest table tracks each invitation and response.']
    ],
    media: mediaUploadTutorialSteps(
      'Reunion media',
      'Upload media associated with this reunion. Images can be included in the published reunion book; video, documents, and audio can also be uploaded.'
    )
  },
  'page-heirloom': {
    heirloom_history: [
      ['Heirloom description', 'This tab contains the description and story of the heirloom.'],
      {
        title: 'Marketplace listing',
        body: 'The highlighted action lets an owner or executor create a marketplace listing when one does not already exist. The price may be left empty when no payment is requested; choose the currency and any exclusive branches in that form.',
        target: '[data-tutorial="heirloom-listing"]',
        requireTarget: '[data-tutorial="heirloom-listing"]'
      },
      {
        title: 'Request an heirloom',
        body: 'When an heirloom is listed and you are not its owner, use the highlighted request action to ask for it.',
        target: '[data-tutorial="heirloom-request"]',
        requireTarget: '[data-tutorial="heirloom-request"]'
      },
      {
        title: 'Modify a listing',
        body: 'An owner or executor can use the highlighted action to modify a listing and make it active or inactive. An inactive listing is removed from the marketplace.',
        target: '[data-tutorial="heirloom-listing"]',
        requireTarget: '[data-tutorial="heirloom-listing"]'
      }
    ],
    provenance: [
      ['Heirloom provenance', 'Provenance records who owned the heirloom, when they owned it, and who owns it now. It can be edited by applicable Branch Administrators, Archivists, and Genealogists.']
    ],
    location: [
      ['Heirloom location history', 'This tab follows the heirloom’s movement over time. To protect privacy, the current location is omitted while the heirloom remains in the family. Only authorized administrators can make changes.']
    ],
    media: mediaUploadTutorialSteps(
      'Heirloom media',
      'Upload the heirloom’s profile image and other supporting media here.'
    ),
    listing_requests: [
      ['Listing requests', 'Owners see family-member requests here. Approval sends the selected requester an invoice with 14 days to pay; successful payment rejects the remaining requests. Rejections send an email notification.']
    ]
  },
  'page-property': {
    property_description: [
      ['Property description', 'This tab contains the property history and description. Anyone can create a property; doing so also requests the Archivist role.']
    ],
    provenance: [
      ['Property provenance', 'Property owners and authorized administrators can update the ownership history.'],
      {
        title: 'Add ownership',
        body: 'Use the highlighted add button to record new ownership. A property purchased through the website can be updated automatically.',
        target: '[data-tutorial="provenance-add-owner"]',
        requireTarget: '[data-tutorial="provenance-add-owner"]'
      },
      ['Save each section', 'Each property section has its own Save button and saves only that section.']
    ],
    media: mediaUploadTutorialSteps(
      'Property media',
      'Media can be uploaded by property points of contact, authorized administrators, and guests.'
    ),
    poi: [
      ['Points of Interest', 'This tab lists nearby points of interest.'],
      {
        title: 'Add a Point of Interest',
        body: 'Authorized administrators, guests, and property points of contact can use the highlighted button to add a nearby point of interest.',
        target: '[data-tutorial="property-add-poi"]',
        requireTarget: '[data-tutorial="property-add-poi"]'
      }
    ],
    availability_calendar: [
      {
        title: 'Availability Calendar',
        body: 'The highlighted calendar shows availability. Only the point of contact can mark unavailable dates; select or drag between a start and end date.',
        target: '[data-tutorial="property-availability-calendar"]',
        requireTarget: '[data-tutorial="property-availability-calendar"]'
      },
      {
        title: 'Contact the property',
        body: 'Use the highlighted Email action to contact the point of contact and request dates.',
        target: '[data-tutorial="property-contact"]',
        requireTarget: '[data-tutorial="property-contact"]'
      },
      {
        title: 'Request a stay',
        body: 'Choose start and end dates in the highlighted calendar, then submit the request. The owner receives an approval request; after approval, the requester has 14 days to pay the invoice.',
        target: '[data-tutorial="property-availability-calendar"]',
        requireTarget: '[data-tutorial="property-contact"]'
      }
    ]
  },
  'page-recipe': {
    ingredients: [
      ['Ingredients', 'Ingredients include the quantity, unit, and ingredient type.']
    ],
    equipment: [
      ['Equipment', 'Recommended equipment can be associated with ingredients, but equipment is optional when creating a recipe.']
    ],
    steps: [
      {
        title: 'Add a recipe step',
        body: 'Only the recipe creator or a Chef can change a recipe. When available, use the highlighted Add Step action to add the step type, written instructions, and an image or video.',
        target: '[data-tutorial="recipe-add-step"]',
        requireTarget: '[data-tutorial="recipe-add-step"]'
      },
      {
        title: 'Cooking View',
        body: 'Open the highlighted Cooking View for a cooking-friendly presentation. Change the serving count there to adjust ingredient quantities.',
        target: '[data-tutorial="recipe-cooking-view"]',
        requireTarget: '[data-tutorial="recipe-cooking-view"]'
      }
    ],
    description: [
      ['Recipe description', 'The recipe description contains its background and primary details. Recipes can be made exclusive to selected branches or by sex.']
    ],
    variations: [
      ['Recipe variations', 'Use Variations for an alternate version of the recipe—for example, an apricot soufflé based on a chocolate soufflé.']
    ]
  },
  'page-store': {
    product: [
      ['Products', 'The product store is an international direct-to-consumer store. Products are fulfilled by a distributor near the delivery country when available.'],
      {
        title: 'Filter products',
        body: 'Use the highlighted filters to filter products by brand or product type.',
        target: '[data-tutorial="store-filters"]',
        requireTarget: '[data-tutorial="store-filters"]'
      },
      {
        title: 'Search products',
        body: 'Use the highlighted search field to find a product directly.',
        target: '[data-tutorial="store-search"]',
        requireTarget: '[data-tutorial="store-search"]'
      },
      ['Choose product options', 'Open a product to start that product page’s tutorial for color, size, quantity, and cart actions.'],
      {
        title: 'Shopping cart',
        body: 'Selected items appear in the cart. Use the highlighted button to open it when you are ready to finish the purchase.',
        target: '[data-tutorial="store-cart"]',
        requireTarget: '[data-tutorial="store-cart"]'
      }
    ],
    marketplace: [
      ['Heirloom marketplace', 'This tab contains heirlooms listed by family members. A requested price is shown when provided; open an heirloom to review it and submit a request.']
    ],
    books: [
      ['Family books', 'Purchase completed family books from this tab.'],
      ['Book options', 'Choose the available cover type, ink, print quality, and print size before ordering.']
    ]
  },
  'page-admin': {
    site_admin: [
      ['Site Administration', 'Site Administrators assign administrator roles but do not edit family archive data directly.'],
      ['Family and external administrators', 'Internal users can be Site Administrators, Branch Administrators, Archivists, or Chefs. External professionals can be Genealogists or Executors.'],
      ['Site activity and approvals', 'Site Administration includes activity history, new-resource change approvals, administrator-role requests, lookup-table options, and email management.']
    ],
    branch_admin: [
      ['Branch Administration', 'Branch Administrators approve new members who have email addresses, designate notable figures, and review proposed branch changes with a difference view.']
    ],
    archivist: [
      ['Archivist', 'Archivists review changes awaiting approval for properties and heirlooms.']
    ],
    executor: [
      ['Executor', 'Executors are external professionals responsible for selling family heirlooms or properties to other family members.']
    ],
    genealogist: [
      ['Genealogist', 'Genealogists are external professionals who can propose changes to family profiles but cannot approve those changes.']
    ],
    chef: [
      ['Chef', 'Chefs review recipe changes awaiting approval. They cannot review a recipe that is exclusive to a branch or sex for which they are not eligible.']
    ],
    internal: [
      ['Family Members', 'Site Administrators assign roles to internal family members. Internal roles include Site Administrator, Branch Administrator, Archivist, and Chef.'],
      ['Branch assignments', 'Selecting the Branch Administrator role makes branch assignments available for that administrator.']
    ],
    external: [
      ['External Users', 'External users are hired professionals. Genealogists can propose profile changes but cannot approve them; Executors manage sales of family heirlooms or properties.']
    ],
    activity: [
      ['Activity', 'The activity feed records who performed an action on the website and when it happened.']
    ],
    change_approvals: [
      ['Change Approvals', 'Review and accept the addition of new branches, heirlooms, properties, reunions, and recipes.']
    ],
    role_requests: [
      ['Role Requests', 'When someone creates a new resource without already being an administrator, their submission also volunteers them for the role that administers that resource.']
    ],
    lookups: [
      ['Lookup Tables', 'Add missing choices used by website dropdown fields from this tab.']
    ],
    email: [
      ['Email management', 'Manage support and system email and verify that messages are being delivered successfully.'],
      ['Incoming support email', 'Open incoming questions with View, review the thread, and reply from the support mailbox.'],
      ['Conversation history', 'The newest message appears in the thread together with outgoing replies from Site Administrators.'],
      ['System email', 'Review system messages and their delivery status. Messages such as GDPR and Welcome emails can be resent when necessary.']
    ]
  }
}

export function pageTabTutorialSteps(guideId, tabKey, label) {
  const configuredSteps = pageTabGuides[guideId]?.[tabKey]

  const mediaFormatGuidance = {
    image: 'Accepted image formats are JPEG, PNG, WebP, HEIC, HEIF, and GIF.',
    document: 'Documents accept PDF files.',
    video: 'Accepted video formats are MP4 and WebM.',
    audio: 'Accepted audio formats are MP3/MPEG and M4A/MP4 audio.'
  }

  if (String(guideId).includes('-tab-media') && mediaFormatGuidance[tabKey]) {
    return [{
      title: `${label} tab`,
      body: `Select the ${label} tab to upload and review this media type. ${mediaFormatGuidance[tabKey]}`
    }]
  }

  if (!configuredSteps?.length) {
    return [{
      title: `${label} tab`,
      body: `Select the ${label} tab to review the tools and information in this section.`
    }]
  }

  return configuredSteps.map(step => {
    if (Array.isArray(step)) {
      const [title, body] = step
      return { title, body }
    }

    return { ...step }
  })
}

export function tutorialSteps() {
  return [
    {
      id: 'header',
      title: 'Header',
      body: 'The Header stays available across the signed-in website. It contains the family crest, family-member search, notifications, accessibility settings, tutorial control, profile menu, and main navigation.',
      target: '.app-header'
    },
    {
      id: 'header-search',
      title: 'Search for a family member',
      body: 'Enter a family member’s name in the search field, then select the matching result to open that person’s profile.',
      target: '[data-tutorial="search"]'
    },
    {
      id: 'header-navigation',
      title: 'Main navigation',
      body: 'Use these links to open Branches, Reunions, Heirlooms, Properties, Recipes, the Store, the Directory, and any Administration tools available to your role. Each section starts its own walkthrough after you select it.',
      target: '[data-tutorial="navigation"]'
    },
    {
      id: 'header-home',
      title: 'Return to the Landing Page',
      body: 'Select the family crest at any time to return to the Landing Page.',
      target: '[data-tutorial="home"]'
    },
    {
      id: 'header-notifications-open',
      title: 'Open Notifications',
      body: 'Select the bell to open Notifications. Notification items can take you directly to the related part of the website.',
      target: '[data-tutorial="notifications-trigger"]',
      activationTarget: '[data-tutorial="notifications-trigger"]'
    },
    {
      id: 'header-notifications-panel',
      title: 'Notification status and actions',
      body: 'A green check marks a successful action, a red X marks a failed action, and a bell marks a general notification. Select a linked notification to open it, remove one with its X, or clear all notifications with the trash icon.',
      target: '[data-tutorial="notifications-panel"]',
      openTarget: '[data-tutorial="notifications-trigger"]',
      closeTargets: ['[data-tutorial="accessibility-trigger"]']
    },
    {
      id: 'header-accessibility-open',
      title: 'Open Accessibility settings',
      body: 'Select the Accessibility icon to open the quick settings shown in the presentation.',
      target: '[data-tutorial="accessibility-trigger"]',
      activationTarget: '[data-tutorial="accessibility-trigger"]',
      closeTargets: ['[data-tutorial="notifications-trigger"]']
    },
    {
      id: 'header-accessibility-display',
      title: 'Display settings',
      body: 'Choose System, Dark, or Light theme; adjust font size; select a colorblind mode; and turn on High Contrast, Reduced Motion, or the Dyslexia Friendly Font. Changes preview immediately and Save Settings stores them.',
      target: '[data-tutorial="accessibility-panel"]',
      activationTarget: '[data-tutorial="accessibility-display"]',
      openTarget: '[data-tutorial="accessibility-trigger"]',
      closeTargets: ['[data-tutorial="notifications-trigger"]']
    },
    {
      id: 'header-accessibility-reader',
      title: 'Reader settings',
      body: 'The Reader tab can read selected page content aloud. Enable it, choose a browser voice, adjust rate, pitch, and volume, and use Test Voice to hear the current choices.',
      target: '[data-tutorial="accessibility-panel"]',
      activationTarget: '[data-tutorial="accessibility-reader"]',
      openTarget: '[data-tutorial="accessibility-trigger"]',
      closeTargets: ['[data-tutorial="notifications-trigger"]']
    },
    {
      id: 'page-home',
      title: 'Landing Page',
      body: 'The Landing Page contains the Family World Map, Family Web, Notable Figures, Historical Properties, and Heirlooms. The walkthrough will now select every tab in that order.',
      target: '[data-tutorial="page-content"]',
      path: '/home',
      activationTarget: '[data-tutorial="home"]',
      closeTargets: ['[data-tutorial="accessibility-trigger"]']
    },
    {
      id: 'restart',
      title: 'You can revisit this tour',
      body: 'Select the Tutorial icon beside Notifications and Accessibility to rerun the tutorial for the page you are currently viewing. Each resource page has its own walkthrough.',
      target: '[data-tutorial="restart"]'
    }
  ]
}

function fallbackKey(userDetailId) {
  return `tf_tutorial_version:${userDetailId}`
}

function readFallbackVersion(userDetailId) {
  if (typeof localStorage === 'undefined' || !userDetailId) return 0
  return Number(localStorage.getItem(fallbackKey(userDetailId)) || 0)
}

function writeFallbackVersion(userDetailId, version) {
  if (typeof localStorage === 'undefined' || !userDetailId) return
  localStorage.setItem(fallbackKey(userDetailId), String(version))
}

function guideFallbackKey(userDetailId, guideId) {
  return `tf_tutorial_guide:${userDetailId}:${guideId}`
}

function hasFallbackGuide(userDetailId, guideId) {
  if (typeof localStorage === 'undefined' || !userDetailId || !guideId) return false
  return localStorage.getItem(guideFallbackKey(userDetailId, guideId)) === '1'
}

function writeFallbackGuide(userDetailId, guideId) {
  if (typeof localStorage === 'undefined' || !userDetailId || !guideId) return
  localStorage.setItem(guideFallbackKey(userDetailId, guideId), '1')
}

export const useTutorialStore = defineStore('tutorial', {
  state: () => ({
    isOpen: false,
    currentIndex: 0,
    steps: [],
    checkedUserDetailId: null,
    checkedGuideIds: [],
    activeGuideId: null,
    startedAutomatically: false
  }),

  getters: {
    currentStep(state) {
      return state.steps[state.currentIndex] || null
    },

    isFirstStep(state) {
      return state.currentIndex === 0
    },

    isLastStep(state) {
      return state.steps.length > 0 && state.currentIndex === state.steps.length - 1
    }
  },

  actions: {
    start({ automatic = false, path = null, fullSite = false } = {}) {
      const siteSteps = tutorialSteps()
      const currentPath = path ?? window.location.pathname
      const pageSteps = pageTutorialSteps(currentPath)
      this.activeGuideId = pageSteps[0].id
      this.steps = fullSite
        ? siteSteps
        : [...pageSteps, siteSteps[siteSteps.length - 1]]
      this.currentIndex = 0
      this.startedAutomatically = automatic
      this.isOpen = true
    },

    startForPage({ automatic = false, path = null } = {}) {
      const currentPath = path ?? window.location.pathname
      const normalizedPath = String(currentPath || '').replace(/\/$/, '') || '/'

      this.start({
        automatic,
        path: currentPath,
        fullSite: normalizedPath === '/home'
      })
    },

    async maybeStart({ path = null } = {}) {
      const auth = useAuthStore()
      const settings = useSettingsStore()
      const userDetailId = auth.user_detail_id
      const [pageStep] = pageTutorialSteps(path ?? window.location.pathname)
      const guideId = pageStep.id

      if (!userDetailId) return
      if (this.isOpen && this.activeGuideId === guideId) return
      if (this.isOpen) this.close()

      if (this.checkedUserDetailId !== userDetailId) {
        this.checkedUserDetailId = userDetailId
        this.checkedGuideIds = []
      }

      if (this.checkedGuideIds.includes(guideId)) return
      this.checkedGuideIds.push(guideId)

      const storedVersion = Number(settings.settings.tutorial_version || 0)
      const localVersion = readFallbackVersion(userDetailId)
      const completedGuides = Array.isArray(settings.settings.tutorial_completed_guides)
        ? settings.settings.tutorial_completed_guides
        : []
      const guideComplete = (
        completedGuides.includes(guideId) ||
        hasFallbackGuide(userDetailId, guideId)
      )

      if (Math.max(storedVersion, localVersion) < CURRENT_TUTORIAL_VERSION) {
        this.startForPage({ automatic: true, path })
        await this.markGuideSeen(guideId)
      } else if (!guideComplete) {
        this.startForPage({ automatic: true, path })
        await this.markGuideSeen(guideId)
      }
    },

    addTabSteps({ afterStepId, groupId, path, tabs = [] }) {
      if (!afterStepId || !path || tabs.length === 0) return
      const resolvedGroupId = groupId || path
      if (this.steps.some(step => (
        step.tabGroupId === resolvedGroupId ||
        (!groupId && !step.tabGroupId && step.tabPath === path)
      ))) return

      const insertAt = this.steps.findIndex(step => step.id === afterStepId)
      if (insertAt < 0) return

      const tabSteps = tabs.flatMap((tab, tabIndex) => {
        const configuredSteps = tab.steps?.length
          ? tab.steps
          : pageTabTutorialSteps(afterStepId, tab.key, tab.label)
        const baseId = `${afterStepId}-tab-${tab.idKey || tab.key || tabIndex}`

        return configuredSteps.map((step, stepIndex) => ({
          id: configuredSteps.length === 1
            ? baseId
            : `${baseId}-${stepIndex + 1}`,
          title: step.title,
          body: step.body,
          target: step.target || tab.panelTarget || tab.target,
          openTarget: tab.target,
          activationTarget: step.activationTarget,
          activateOnCurrentPath: step.activateOnCurrentPath,
          scrollTargetIntoView: step.scrollTargetIntoView,
          inputTarget: step.inputTarget,
          inputValue: step.inputValue,
          submitTarget: step.submitTarget,
          optionalActivationTarget: step.optionalActivationTarget,
          dispatchTarget: step.dispatchTarget,
          dispatchEvent: step.dispatchEvent,
          requireTarget: step.requireTarget,
          requireTargetImmediately: step.requireTargetImmediately,
          tabPath: path,
          tabGroupId: resolvedGroupId
        }))
      })

      this.steps.splice(insertAt + 1, 0, ...tabSteps)
    },

    next() {
      if (this.isLastStep) {
        return this.complete()
      }

      this.currentIndex += 1
    },

    back() {
      if (!this.isFirstStep) this.currentIndex -= 1
    },

    skip() {
      return this.complete()
    },

    async complete() {
      this.isOpen = false
      await this.markGuideSeen(this.activeGuideId)
    },

    async markGuideSeen(guideId) {
      const auth = useAuthStore()
      const settings = useSettingsStore()
      const userDetailId = auth.user_detail_id
      const completedGuides = new Set(
        Array.isArray(settings.settings.tutorial_completed_guides)
          ? settings.settings.tutorial_completed_guides
          : []
      )

      if (guideId) completedGuides.add(guideId)

      const patch = {
        tutorial_version: CURRENT_TUTORIAL_VERSION,
        tutorial_completed_guides: [...completedGuides]
      }

      writeFallbackVersion(userDetailId, CURRENT_TUTORIAL_VERSION)
      writeFallbackGuide(userDetailId, guideId)
      settings.setLocal(patch)

      try {
        await settings.save(patch)
      } catch (error) {
        console.warn('Tutorial page tracker could not be saved to the server.', error)
      }
    },

    close() {
      this.isOpen = false
    }
  }
})
